import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { banners, comics, categories, faqItems } from '@/mocks/comics';

export default function Home() {
  const [currentBanner, setCurrentBanner] = useState(0);
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  const nextBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextBanner, 5000);
    return () => clearInterval(timer);
  }, [nextBanner]);

  const topComics = comics.slice(0, 8);
  const newestComics = [...comics].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  const topCategories = categories.slice(0, 4);

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '妖精动漫 - 韩漫日漫国漫大全免费在线阅读',
    description: '妖精动漫汇集韩漫、日漫、国漫大全，韩国漫画、日本漫画、国产漫画免费全集在线阅读',
    url: 'https://yaojingdongman.com',
    isPartOf: {
      '@type': 'WebSite',
      name: '妖精动漫',
      url: 'https://yaojingdongman.com',
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: topComics.map((comic, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'ComicSeries',
          name: comic.title,
          author: { '@type': 'Person', name: comic.author },
          url: `https://yaojingdongman.com/comic/${comic.id}`,
        },
      })),
    },
  };

  return (
    <>
      <SEOHead
        title="妖精动漫 | 妖精动漫韩漫日漫国漫大全 | 韩国漫画日本漫画国产漫画免费全集阅读"
        description="妖精动漫汇集韩漫、日漫、国漫大全，韩国漫画、日本漫画、国产漫画免费全集在线阅读，下拉式流畅翻页，好看漫画追更不停。妖精动漫韩漫、妖精动漫日漫、妖精动漫国漫免费阅读。"
        keywords="妖精动漫,妖精动漫韩漫,妖精动漫日漫,妖精动漫国漫,妖精动漫韩国漫画,妖精动漫日本漫画,妖精动漫国产漫画,妖精动漫大全,妖精动漫免费阅读,妖精动漫在线阅读,妖精动漫全集,韩国漫画,日本漫画,国产漫画"
        canonicalUrl="https://yaojingdongman.com"
        schema={homeSchema}
      />

      <Breadcrumb items={[{ label: '首页' }]} />

      {/* Hero Banner */}
      <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
        {banners.map((banner, index) => (
          <div
            key={banner.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === currentBanner ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={banner.image}
              alt={banner.title}
              className="w-full h-full object-cover object-top"
              width={1600}
              height={600}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
              <div className="max-w-7xl mx-auto w-full">
                <h2 className="text-white text-2xl md:text-4xl font-heading font-bold mb-3 drop-shadow-lg max-w-2xl">
                  {banner.title}
                </h2>
                <Link
                  to={`/comic/${banner.comicId}`}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-all duration-200 no-underline whitespace-nowrap"
                >
                  立即阅读
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        ))}
        <div className="absolute bottom-6 right-6 md:right-12 flex gap-2 z-10">
          {banners.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentBanner(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                i === currentBanner ? 'bg-primary-500 w-8' : 'bg-white/60 hover:bg-white/90'
              }`}
              aria-label={`切换到第${i + 1}张横幅`}
            />
          ))}
        </div>
      </section>

      {/* Category Navigation */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
            探索漫画世界
          </h2>
          <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
            韩漫、日漫、国漫全面覆盖，总有你喜欢的类型
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {topCategories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="group p-5 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-300 no-underline"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-3 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                <i className={`${cat.icon} text-xl`}></i>
              </div>
              <h3 className="text-base font-semibold text-foreground-900 mb-1 whitespace-nowrap">{cat.name}</h3>
              <p className="text-xs text-foreground-500">{cat.count}部作品</p>
            </Link>
          ))}
        </div>
        <div className="text-center mt-6">
          <Link
            to="/category"
            className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium no-underline transition-colors whitespace-nowrap"
          >
            查看全部分类
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* Hot Comics */}
      <section className="bg-background-100/70 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                热门推荐
              </h2>
              <p className="text-sm text-foreground-500">大家都在看的漫画</p>
            </div>
            <Link
              to="/category"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium no-underline whitespace-nowrap hidden sm:inline-flex items-center gap-1"
            >
              查看更多
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {topComics.map((comic, index) => (
              <ComicCard key={comic.id} comic={comic} index={index + 1} />
            ))}
          </div>
        </div>
      </section>

      {/* Latest Updates */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                最新更新
              </h2>
              <p className="text-sm text-foreground-500">刚刚更新的热门漫画</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {newestComics.slice(0, 8).map((comic) => (
              <Link
                key={comic.id}
                to={`/comic/${comic.id}`}
                className="flex items-center gap-3 p-3 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/30 transition-all duration-200 no-underline group"
              >
                <img
                  src={comic.cover}
                  alt={comic.title}
                  className="w-16 h-20 object-cover rounded-md flex-shrink-0"
                  width={64}
                  height={80}
                />
                <div className="min-w-0 flex-1">
                  <h4 className="text-sm font-semibold text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                    {comic.title}
                  </h4>
                  <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-xs px-1.5 py-0.5 rounded bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                      {comic.tags[0]}
                    </span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">更新至{comic.chapters.length}话</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background-100/70 py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              常见问题
            </h2>
            <p className="text-sm text-foreground-500">关于妖精动漫你想知道的都在这里</p>
          </div>
          <div className="space-y-3">
            {faqItems.map((item, index) => (
              <div
                key={index}
                className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden"
              >
                <button
                  onClick={() => setFaqOpen(faqOpen === index ? null : index)}
                  className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
                >
                  <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                    {item.question}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-300 flex-shrink-0 ${
                      faqOpen === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                <div
                  className={`transition-all duration-300 overflow-hidden ${
                    faqOpen === index ? 'max-h-96 pb-4 md:pb-5 px-4 md:px-5' : 'max-h-0'
                  }`}
                >
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function ComicCard({ comic, index }: { comic: typeof comics[0]; index: number }) {
  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 no-underline"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={comic.cover}
          alt={comic.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          width={300}
          height={400}
        />
        <span className="absolute top-2 left-2 w-7 h-7 flex items-center justify-center rounded-lg bg-primary-500/90 text-background-50 text-xs font-bold">
          {index}
        </span>
        {comic.status === '已完结' && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded bg-accent-500/90 text-background-50 text-xs font-medium">
            完结
          </span>
        )}
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
          {comic.title}
        </h3>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="text-xs text-foreground-500">{comic.author}</span>
          <span className="text-xs text-foreground-300">|</span>
          <span className="text-xs text-accent-600 font-medium flex items-center gap-0.5">
            <i className="ri-star-fill text-[10px]"></i>
            {comic.rating}
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {comic.tags.map((tag) => (
            <span
              key={tag}
              className="text-[10px] px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700 whitespace-nowrap"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}

export function Widget() {
  return null;
}